import React, { useState, useEffect } from 'react';
import { Modal, DatePicker, Button, message, Alert, Space } from 'antd';
import { getId } from '../../helpers/file';
import Cart from '../Header/Cart';
import { useTranslation } from '../../hooks/useTranslation';
import dayjs from 'dayjs';

interface ExpireTimeModalProps {
  /** 是否显示弹框 */
  open: boolean;
  /** 关闭弹框的回调 */
  onCancel: () => void;
  /** 记录ID */
  recordId: number;
  /** 当前过期时间 */
  currentExpireTime?: Date | null;
  /** 保存成功后的回调 */
  onSuccess?: () => void;
  /** 是否是VIP用户 */
  isVip: boolean;
}

/**
 * 过期时间编辑弹框组件
 * 
 * @author dongdong.fdd
 * @date 2026/01/11
 */
const ExpireTimeModal: React.FC<ExpireTimeModalProps> = ({
  open,
  onCancel,
  recordId,
  currentExpireTime,
  onSuccess,
  isVip,
}) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCart, setShowCart] = useState(false);

  // 初始化日期选择器的值
  useEffect(() => {
    if (open && currentExpireTime) {
      setSelectedDate(dayjs(currentExpireTime));
    } else if (open) {
      // 默认设置为一年后
      setSelectedDate(dayjs().add(1, 'year'));
    }
  }, [open, currentExpireTime]);

  // 保存过期时间
  const handleSave = async () => {
    if (!selectedDate) {
      message.error(t('expireTime.selectRequired'));
      return;
    }

    // 校验日期必须在今天之后
    if (selectedDate.isBefore(dayjs(), 'day')) {
      message.error(t('expireTime.mustBeAfterToday'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/img/update-expire-time`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          id: String(recordId),
          uid: getId() || '',
          expireTime: String(selectedDate.valueOf()),
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        message.success(t('expireTime.setSuccess'));
        onSuccess?.();
        onCancel();
      } else {
        message.error(result.message || t('expireTime.setFailed'));
      }
    } catch (error) {
      console.error('设置过期时间失败:', error);
      message.error(t('expireTime.setFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 非VIP用户显示提示
  if (!isVip) {
    return (
      <>
        <Modal
          title={t('expireTime.title')}
          open={open}
          onCancel={onCancel}
          footer={null}
          width={400}
        >
          <Alert
            message={t('expireTime.vipOnly')}
            description={t('expireTime.vipOnlyMessage')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <div style={{ textAlign: 'center' }}>
            <Button
              type="primary"
              onClick={() => setShowCart(true)}
              style={{ marginRight: 8 }}
            >
              {t('expireTime.buyVip')}
            </Button>
            <Button onClick={onCancel}>{t('common.close')}</Button>
          </div>
        </Modal>

        {/* VIP 中心弹框 */}
        <Modal
          title={t('expireTime.vipCenter')}
          footer={null}
          width="80%"
          open={showCart}
          onCancel={() => setShowCart(false)}
        >
          <Cart />
        </Modal>
      </>
    );
  }

  // VIP用户显示日期选择器
  return (
    <Modal
      title={t('expireTime.title')}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={400}
    >
      <div style={{ marginBottom: 16 }}>
        <p style={{ marginBottom: 8, color: '#666' }}>
          {t('expireTime.description')}
        </p>
        <DatePicker
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          style={{ width: '100%' }}
          disabledDate={(current) => current && current < dayjs().startOf('day')}
          format="YYYY-MM-DD"
          placeholder={t('expireTime.datePlaceholder')}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space wrap>
          <Button size="small" onClick={() => setSelectedDate(dayjs().add(7, 'day'))}>
            {t('expireTime.oneWeek')}
          </Button>
          <Button size="small" onClick={() => setSelectedDate(dayjs().add(30, 'day'))}>
            {t('expireTime.thirtyDays')}
          </Button>
          <Button size="small" onClick={() => setSelectedDate(dayjs().add(90, 'day'))}>
            {t('expireTime.ninetyDays')}
          </Button>
          <Button size="small" onClick={() => setSelectedDate(dayjs().add(180, 'day'))}>
            {t('expireTime.oneEightyDays')}
          </Button>
          <Button size="small" onClick={() => setSelectedDate(dayjs().add(1, 'year'))}>
            {t('expireTime.oneYear')}
          </Button>
          <Button size="small" onClick={() => setSelectedDate(dayjs().add(5, 'year'))}>
            {t('expireTime.fiveYears')}
          </Button>
          <Button size="small" onClick={() => setSelectedDate(dayjs().add(10, 'year'))}>
            {t('expireTime.tenYears')}
          </Button>
          <Button size="small" onClick={() => setSelectedDate(dayjs().add(20, 'year'))}>
            {t('expireTime.twentyYears')}
          </Button>
        </Space>
      </div>

      <div style={{ textAlign: 'right' }}>
        <Button onClick={onCancel} style={{ marginRight: 8 }}>
          {t('common.cancel')}
        </Button>
        <Button type="primary" onClick={handleSave} loading={loading}>
          {t('common.save')}
        </Button>
      </div>
    </Modal>
  );
};

export default ExpireTimeModal;
